import React, { useMemo } from 'react';
import { Modal, PieChart, DataTable, AccessibleDataTableColumn, formatDate } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, TranslatedComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { useLocalizedStrings } from '../../../../Localization';
import { AdminPanelBarChart } from './AdminPanelBarChart';
import { AdminPanelLineChart } from './AdminPanelLineChart';

type ChartDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
  component: SupportedDashboardComponent;
  queryResult?: TranslatedComponentResult;
  chartHeight?: number;
};

export const ChartDataModal: React.FC<ChartDataModalProps> = ({
  isOpen,
  onClose,
  component,
  queryResult,
  chartHeight = 400,
}) => {
  const L = useLocalizedStrings();

  const chartTitle = useMemo(() => {
    if ('title' in component && component.title) {
      return typeof component.title === 'string' ? component.title : component.title['en'];
    }
    return 'Chart Data';
  }, [component]);

  const chartElement = useMemo(() => {
    if (!queryResult) return null;

    if (component.kind === 'Bar Chart') {
      return (
        <AdminPanelBarChart
          component={component}
          queryResult={queryResult}
          height={chartHeight}
          tableCaption={chartTitle}
        />
      )
    }

    if (component.kind === 'Line Chart') {
      return (
        <AdminPanelLineChart
          component={component}
          queryResult={queryResult}
          height={chartHeight}
          tableCaption={chartTitle}
          showDataTable={false}
        />
      );
    }

    if (component.kind === 'Pie Chart') {
      const nameKey = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const dataKey = queryResult.summaryKey;

      let processedData = Array.isArray(queryResult.data) ? queryResult.data.map((datum: Record<string, any>) => ({
        ...datum,
        [dataKey]: datum[dataKey] != null ? Number(datum[dataKey]) : null
      })) : [];

      // Handle data summarization for pie charts (same logic as in DashboardComponentCard)
      if (processedData.length > 5) {
        processedData.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0));
        const topFour = processedData.slice(0, 4);
        const remaining = processedData.slice(4);
        const otherValue = remaining.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

        const otherSegment = {
          [nameKey]: L.admin_panel.dashboards.other,
          [dataKey]: otherValue
        };

        processedData = [...topFour, otherSegment];
      }

      return (
        <PieChart
          showDataTable={false}
          height={chartHeight}
          data={processedData}
          dataKey={dataKey}
          nameKey={nameKey}
          tableCaption={chartTitle}
        />
      );
    }

    return null;
  }, [component, queryResult, chartHeight, chartTitle, L]);

  const tableColumns = useMemo((): AccessibleDataTableColumn[] => {
    if (!queryResult) return [];

    if (component.kind === 'Bar Chart') {
      const primaryGroupField = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const summaryKey = queryResult.summaryKey || '';
      const barsConfig = (component as any).bars;
      const hasSecondaryGrouping = barsConfig && barsConfig.kind === 'Grouped' && barsConfig.values?.[0]?.field;

      const columns: AccessibleDataTableColumn[] = [
        {
          key: primaryGroupField,
          label: component.xAxisLabel || primaryGroupField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }
      ];

      // If we have secondary grouping, add a column for each secondary value
      if (hasSecondaryGrouping) {
        const secondaryField = barsConfig.values[0].field;
        const secondaryValueSet = new Set<string>();

        queryResult.data.forEach((row: any) => {
          const secondaryValue = String(row[secondaryField] || 'null');
          secondaryValueSet.add(secondaryValue);
        });

        Array.from(secondaryValueSet).forEach(secondaryValue => {
          columns.push({
            key: secondaryValue,
            label: secondaryValue,
            format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
          });
        });
      } else {
        // Single grouping - just add the summary column
        columns.push({
          key: summaryKey,
          label: component.yAxisLabel || summaryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        });
      }

      return columns;
    }

    if (component.kind === 'Line Chart') {
      let lineGroupKey = ''
      let bucketFormat: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' | undefined
      if (component.groups[0].kind === 'Bucket Date') {
        const bucketGroup = component.groups[0]
        const bucket = bucketGroup.bucket.charAt(0).toUpperCase() + bucketGroup.bucket.slice(1)
        bucketFormat = bucketGroup.bucket

        if (bucketGroup.value.kind === 'As Date' && bucketGroup.value.value.kind === 'Field') {
          const field = bucketGroup.value.value.field
          lineGroupKey = `${bucket} of ${field} (As Date)`
        }
      }
      const lineSummaryKey = queryResult.summaryKey || ''

      return [
        {
          key: lineGroupKey,
          label: component.xAxisLabel || lineGroupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: any) => value ? formatDate(value, bucketFormat) : ''
        },
        {
          key: lineSummaryKey,
          label: component.yAxisLabel || lineSummaryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        }
      ];
    }

    if (component.kind === 'Pie Chart') {
      const nameKey = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const dataKey = queryResult.summaryKey;

      return [
        {
          key: nameKey,
          label: nameKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        },
        {
          key: dataKey,
          label: dataKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        }
      ];
    }

    return [];
  }, [component, queryResult]);

  const tableData = useMemo(() => {
    if (!queryResult) return [];

    // For bar charts with secondary grouping, pivot the data to match the chart
    if (component.kind === 'Bar Chart') {
      const barsConfig = (component as any).bars;
      const hasSecondaryGrouping = barsConfig && barsConfig.kind === 'Grouped' && barsConfig.values?.[0]?.field;

      if (hasSecondaryGrouping) {
        const primaryGroupField = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
        const secondaryField = barsConfig.values[0].field;
        const summaryKey = queryResult.summaryKey || '';

        // Group data by primary field and pivot secondary field
        const groupedData: Record<string, Record<string, any>> = {};
        const secondaryValueSet = new Set<string>();

        queryResult.data.forEach((row: any) => {
          const primaryValue = String(row[primaryGroupField] || 'Unknown');
          const secondaryValue = String(row[secondaryField] || 'null');
          const aggregateValue = Number(row[summaryKey]) || 0;

          if (!groupedData[primaryValue]) {
            groupedData[primaryValue] = { [primaryGroupField]: primaryValue };
          }

          secondaryValueSet.add(secondaryValue);

          if (!groupedData[primaryValue][secondaryValue]) {
            groupedData[primaryValue][secondaryValue] = 0;
          }
          groupedData[primaryValue][secondaryValue] += aggregateValue;
        });

        // Fill in missing secondary values with 0
        const result = Object.values(groupedData).map(row => {
          const filledRow = { ...row };
          secondaryValueSet.forEach(secondaryValue => {
            if (!(secondaryValue in filledRow)) {
              filledRow[secondaryValue] = 0;
            }
          });
          return filledRow;
        });

        return result;
      }
    }

    // For other cases, use the raw data
    if (!Array.isArray(queryResult.data)) return [];
    return queryResult.data;
  }, [component, queryResult]);

  if (!queryResult || (component.kind !== 'Bar Chart' && component.kind !== 'Pie Chart' && component.kind !== 'Line Chart')) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => onClose()}
      title={chartTitle}
      size="fullscreen"
      className="max-w-7xl"
    >
      <div className="space-y-6">
        {/* Chart Section */}
        {chartElement}

        {/* Data Table Section */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="bg-white rounded border max-h-96 overflow-auto">
            <DataTable
              data={tableData}
              columns={tableColumns.map(col => ({
                header: col.label,
                accessorKey: col.key,
                cell: col.format ? ({ getValue, row }: any) => col.format!(getValue(), row.original) : ({ getValue }: any) => getValue()
              }))}
              pageSize={10}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};